import { useState, useMemo, memo, useContext } from 'react';

import { ClientInfoContext } from '../../../../../client-info';
import { WithLoader } from '../../../../../../../common/loader';

import { useEditAccount, useOtpVerify } from './hooks';
import { UPDATE_ACCOUNT_DIALOG_STEPS, AccountInfo, OtpValue } from './interfaces';
import { EditAccountForm, OtpVerificationPhoneAndUssdForm } from './steps';

interface Props {
  handleClose: () => void;
  handleUnlock: () => void;
}

const AccountInformation = ({ handleClose, handleUnlock }: Props) => {
  const { editAccountLoading, editAccountError, handleEditAccount } = useEditAccount();
  const { otpVerifyLoading, otpVerifyError, handleOtpVerify } = useOtpVerify();
  const { clientInfoLoading, clientInfo, getClientInfo } = useContext(ClientInfoContext);

  const [step, setStep] = useState<UPDATE_ACCOUNT_DIALOG_STEPS>(UPDATE_ACCOUNT_DIALOG_STEPS.EDIT_FORM);
  const [ussdShortcodeToDial, setUssdShortcodeToDial] = useState<string>();
  const [userMsisdn, setUserMsisdn] = useState<string>();
  const [userEditedSuccessfully, setUserEditedSuccessfully] = useState<boolean>(false);
  const DialogForm = useMemo(() => {
    switch (step) {
      case UPDATE_ACCOUNT_DIALOG_STEPS.EDIT_FORM:
        handleUnlock();

        return (
          <EditAccountForm
            initialValues={clientInfo}
            userEditedSuccessfully={userEditedSuccessfully}
            onSubmit={(data: AccountInfo) => {
              handleEditAccount(
                data,
                (newStep: UPDATE_ACCOUNT_DIALOG_STEPS, ussdShortcode?: string, msisdn?: string) => {
                  setStep(newStep);
                  setUserEditedSuccessfully(true);
                  getClientInfo();
                  setUssdShortcodeToDial(ussdShortcode);
                  setUserMsisdn(msisdn);
                }
              );
            }}
            handleSecondary={() => {
              setUserEditedSuccessfully(false);
              getClientInfo();
            }}
            loading={editAccountLoading}
            error={editAccountError}
          />
        );
      case UPDATE_ACCOUNT_DIALOG_STEPS.OTP_VERIFICATION_SMS_OR_SMS_AND_USSD:
        return (
          <OtpVerificationPhoneAndUssdForm
            ussdShortcodeToDial={ussdShortcodeToDial}
            userMsisdn={userMsisdn}
            handleClose={handleClose}
            loading={otpVerifyLoading}
            onSubmit={(otp: OtpValue) => {
              handleOtpVerify(otp, () => {
                getClientInfo();
                setUserEditedSuccessfully(true);
                setStep(UPDATE_ACCOUNT_DIALOG_STEPS.EDIT_FORM);
              });
            }}
            error={otpVerifyError}
          />
        );
      default:
        handleClose();
        return null;
    }
  }, [
    clientInfo,
    editAccountError,
    editAccountLoading,
    getClientInfo,
    handleClose,
    handleEditAccount,
    handleOtpVerify,
    handleUnlock,
    otpVerifyError,
    otpVerifyLoading,
    step,
    userEditedSuccessfully,
    userMsisdn,
    ussdShortcodeToDial
  ]);

  return (
    <WithLoader height="200px" loading={clientInfoLoading}>
      {DialogForm}
    </WithLoader>
  );
};

export default memo(AccountInformation);
